import { create, type StoreApi } from 'zustand';
import { converse, type ConverseCandidate, type ConverseResult } from '../lib/api/converse.js';
import { log } from '../lib/log.js';
import { saveCurrentSession, clearCurrentSession, HISTORY_SCHEMA_VERSION } from '../lib/history.js';

export type Phase = 'idle' | 'awaiting' | 'asking' | 'concluded' | 'error';

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  imageDataUrl?: string;
  at: number;
}

export interface WizardState {
  phase: Phase;
  sessionId: string | null;
  messages: ChatMessage[];
  chips: string[];
  candidates: ConverseCandidate[];
  result: ConverseResult | null;
  error: string | null;
  version: number;

  sendTurn(input: { text?: string; imageBase64?: string; imageDataUrl?: string }): Promise<void>;
  reset(): void;
}

function bump(set: StoreApi<WizardState>['setState']): void {
  set((s) => ({ version: s.version + 1 }));
}

export function createWizardStore() {
  return create<WizardState>((set, get) => ({
    phase: 'idle',
    sessionId: null,
    messages: [],
    chips: [],
    candidates: [],
    result: null,
    error: null,
    version: 0,

    async sendTurn(input) {
      const text = (input.text ?? '').trim();
      if (text.length === 0 && !input.imageBase64) return;

      const userMsg: ChatMessage = {
        role: 'user',
        text: text || (input.imageBase64 ? '📷 Photo' : ''),
        imageDataUrl: input.imageDataUrl,
        at: Date.now(),
      };
      set({ messages: [...get().messages, userMsg], phase: 'awaiting', chips: [], error: null });
      bump(set);

      try {
        const res = await converse({
          sessionId: get().sessionId ?? undefined,
          userMessage: text || undefined,
          imageBase64: input.imageBase64,
        });
        const assistantMsg: ChatMessage = { role: 'assistant', text: res.assistantMessage, at: Date.now() };
        set({
          sessionId: res.sessionId,
          messages: [...get().messages, assistantMsg],
          chips: res.chips ?? [],
          candidates: res.candidates ?? [],
          phase: res.phase === 'concluded' ? 'concluded' : 'asking',
          result: res.result ?? null,
        });
        bump(set);
        saveCurrentSession({
          schemaVersion: HISTORY_SCHEMA_VERSION,
          sessionId: res.sessionId,
          phase: get().phase,
          messages: get().messages.map((m) => ({ role: m.role, text: m.text, at: m.at })),
        });
      } catch (err) {
        log.warn('wizard.sendTurn failed', err);
        set({ phase: 'error', error: (err as Error).message });
        bump(set);
      }
    },

    reset() {
      clearCurrentSession();
      set({
        phase: 'idle', sessionId: null, messages: [], chips: [],
        candidates: [], result: null, error: null, version: 0,
      });
    },
  }));
}

export const useWizardStore = createWizardStore();
